import React, {useContext, useEffect, useMemo, useState} from "react";
import {AuthContext} from "../AuthContext";
import {useNavigate} from "react-router-dom";
import {getAppointments} from "../api";
import {toISODate} from "../utils";

function StatusBadge({status}) {
    const cls = {
        REQUESTED: "bg-yellow-100 text-yellow-800",
        CONFIRMED: "bg-green-100 text-green-800",
        RESCHEDULED: "bg-blue-100 text-blue-800",
        CANCELLED: "bg-gray-200 text-gray-700",
    }[status] || "bg-gray-100 text-gray-800";

    const label = {
        REQUESTED: "ANGEFRAGT",
        CONFIRMED: "BESTÄTIGT",
        RESCHEDULED: "GEÄNDERT",
        CANCELLED: "ABGESAGT",
    }[status] || status;

    return <span className={`px-2 py-1 rounded text-xs font-semibold ${cls}`}>{label}</span>;
}

export default function AppointmentCalendar() {
    const {token, logout} = useContext(AuthContext);
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [current, setCurrent] = useState(new Date());

    useEffect(() => {
        if (!token) navigate("/login");
    }, [token, navigate]);

    const refresh = async () => {
        setLoading(true);
        setError("");
        try {
            const data = await getAppointments(token);
            setItems(Array.isArray(data) ? data : []);
        } catch (e) {
            if (e.message === "unauthorized") {
                logout();
                navigate("/login");
            } else {
                setError("Konnte Termine nicht laden.");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        refresh();
    }, []);

    const startOfWeek = useMemo(() => {
        const d = new Date(current);
        const day = d.getDay();
        const diff = d.getDate() - day + (day === 0 ? -6 : 1);
        return new Date(d.setDate(diff));
    }, [current]);

    const weekDays = useMemo(() => {
        return Array.from({length: 7}, (_, i) => {
            const d = new Date(startOfWeek);
            d.setDate(startOfWeek.getDate() + i);
            return d;
        });
    }, [startOfWeek]);

    const byDay = useMemo(() => {
        const map = {};
        for (const a of items) {
            (map[a.date] = map[a.date] || []).push(a);
        }
        for (const k of Object.keys(map)) {
            map[k].sort((a, b) => a.time.localeCompare(b.time));
        }
        return map;
    }, [items]);

    const hours = useMemo(() => Array.from({length: 11}, (_, i) => i + 8), []);

    const formatRange = () => {
        const end = new Date(startOfWeek);
        end.setDate(startOfWeek.getDate() + 6);
        const opts = {day: "2-digit", month: "2-digit"};
        return `${startOfWeek.toLocaleDateString("de-DE", opts)} - ${end.toLocaleDateString("de-DE", opts)}`;
    };

    return (
        <section className="p-4 min-h-screen bg-gray-50">
            <h2 className="text-2xl font-bold mb-4">Kalender</h2>
            <div className="flex items-center gap-4 mb-4">
                <button
                    className="px-3 py-1 bg-blue-600 text-white rounded"
                    onClick={() => setCurrent(new Date(current.setDate(current.getDate() - 7)))}
                >
                    &lt;
                </button>
                <span className="font-semibold">{formatRange()}</span>
                <button
                    className="px-3 py-1 bg-blue-600 text-white rounded"
                    onClick={() => setCurrent(new Date(current.setDate(current.getDate() + 7)))}
                >
                    &gt;
                </button>
            </div>
            {error && (
                <div className="mb-4 p-3 rounded bg-red-50 text-red-700 border border-red-200">{error}</div>
            )}
            {loading ? (
                <div>Lädt…</div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="min-w-full border-collapse">
                        <thead>
                        <tr>
                            <th className="border p-2 text-left">Zeit</th>
                            {weekDays.map((d) => (
                                <th key={toISODate(d)} className="border p-2 text-left">
                                    {d.toLocaleDateString("de-DE", {weekday: "short", day: "2-digit", month: "2-digit"})}
                                </th>
                            ))}
                        </tr>
                        </thead>
                        <tbody>
                        {hours.map((h) => (
                            <tr key={h}>
                                <td className="border p-2 align-top text-sm font-medium">{`${String(h).padStart(2, "0")}:00`}</td>
                                {weekDays.map((d) => {
                                    const dayKey = toISODate(d);
                                    const list = byDay[dayKey] || [];
                                    const timeStr = `${String(h).padStart(2, "0")}:00`;
                                    const matches = list.filter((a) => {
                                        const start = parseInt(a.time.substring(0,2), 10);
                                        const dur = a.duration || 1;
                                        return h >= start && h < start + dur;
                                    });
                                    return (
                                        <td key={dayKey + timeStr} className="border p-2 align-top text-sm">
                                            {matches.map((a) => {
                                                const details = `Name: ${a.name}\nE-Mail: ${a.email}\nTelefon: ${a.telefon || '-'}` +
                                                    `\nKFZ: ${a.kfz}\nService: ${a.service || '-'}`;
                                                return (
                                                    <div
                                                        key={a.id}
                                                        className="mb-1 cursor-pointer flex items-center gap-1 hover:bg-blue-50 rounded px-1"
                                                        title={details}
                                                        onClick={() => navigate('/appointments', {state: {scrollToId: a.id}})}
                                                    >
                                                        <StatusBadge status={a.status}/>
                                                        <span>{a.name}</span>
                                                    </div>
                                                );
                                            })}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
}

